import type { OnboardingStepKey } from '../../config/onboardingRegistry'
import { ONBOARDING_STEPS } from '../../config/onboardingRegistry'
import {
  guideCurrentStepNumber,
  guideStepTotal,
  ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE,
  ONBOARDING_GUIDE_SEQUENCE,
} from './onboardingGuideConfig'
import { pickOffersForOnboardingStep } from './pickOnboardingOffers'
import { isOfferMgmtHubStep } from './onboardingOfferManagementGating'

/** Kolejność modułów w trybie guided (przewodnik podstawowy). */
export const ONBOARDING_GUIDED_SEQUENCE: OnboardingStepKey[] = ONBOARDING_GUIDE_SEQUENCE

export const ONBOARDING_GUIDED_QUERY = 'guided'

/** Hub przewodnika — dashboard z checklistą kolejnego kroku. */
export const ONBOARDING_HUB_PATH = '/dashboard'

export function isGuidedOnboardingSearch(search: string): boolean {
  return new URLSearchParams(search).get('onboarding') === ONBOARDING_GUIDED_QUERY
}

/** Usuwa parametry sesji przewodnika z URL (np. po rezygnacji). */
export function stripGuidedOnboardingParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete('onboarding')
  next.delete('onboardingStep')
  return next
}

export function stepDefForKey(key: OnboardingStepKey) {
  return ONBOARDING_STEPS.find((s) => s.key === key)
}

export function nextGuidedStepKey(
  completedKeys: Set<OnboardingStepKey>,
): OnboardingStepKey | null {
  for (const key of ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE) {
    if (!completedKeys.has(key)) return key
  }
  return null
}

/** Completed step keys from /onboarding/status payload. */
export function completedOnboardingKeys(
  status?: { steps?: Array<{ key: OnboardingStepKey; completed: boolean }> } | null,
): Set<OnboardingStepKey> {
  return new Set((status?.steps ?? []).filter((s) => s.completed).map((s) => s.key))
}

/** Steps that don't need a marketplace account to build a guided URL. */
export function guidedStepNeedsAccount(stepKey: OnboardingStepKey): boolean {
  return stepKey !== 'connect_allegro' && stepKey !== 'create_account'
}

export function guidedStepIndex(stepKey: OnboardingStepKey): number {
  return guideCurrentStepNumber(stepKey)
}

export function guidedStepTotal(): number {
  return guideStepTotal()
}

function appendGuidedParams(path: string, stepKey?: OnboardingStepKey): string {
  const url = new URL(path, window.location.origin)
  url.searchParams.set('onboarding', ONBOARDING_GUIDED_QUERY)
  if (stepKey) {
    url.searchParams.set('onboardingStep', stepKey)
  }
  return `${url.pathname}${url.search}`
}

export function buildOfferManagementGuidedPath(
  accountId: number,
  offerIds: string[],
  stepKey: OnboardingStepKey,
): string {
  const params = new URLSearchParams({
    accountId: String(accountId),
    offerIds: offerIds.join(','),
    entryPoint: 'offer_management',
    onboarding: ONBOARDING_GUIDED_QUERY,
    onboardingStep: stepKey,
  })
  return `/allegro/offer-management?${params.toString()}`
}

/** Buduje URL do kroku guided — hub kroków idzie przez zarządzanie ofertami. */
export async function buildGuidedStepPath(
  stepKey: OnboardingStepKey,
  accountId?: number,
): Promise<string> {
  const def = stepDefForKey(stepKey)
  if (!def?.route) return ONBOARDING_HUB_PATH

  // Connect / brak konta — sam route z parametrami guided, bez picka ofert.
  if (!guidedStepNeedsAccount(stepKey) || !accountId) {
    return appendGuidedParams(def.route, stepKey)
  }

  if (isOfferMgmtHubStep(stepKey)) {
    try {
      // Po świeżym OAuth sync ofert bywa pusty — krótkie ponowienia przed nawigacją.
      let offerIds: string[] = []
      for (let attempt = 0; attempt < 4; attempt += 1) {
        offerIds = await pickOffersForOnboardingStep(accountId, stepKey)
        if (offerIds.length > 0) break
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, 1500))
        }
      }
      if (offerIds.length === 0) {
        return appendGuidedParams('/allegro/offer-management', stepKey)
      }
      return buildOfferManagementGuidedPath(accountId, offerIds, stepKey)
    } catch {
      return appendGuidedParams('/allegro/offer-management', stepKey)
    }
  }

  return appendGuidedParams(def.route, stepKey)
}

/** Czy krok checklisty jest zablokowany (przyszły moduł). */
export function isGuidedChecklistStepLocked(
  stepKey: OnboardingStepKey,
  completedKeys: Set<OnboardingStepKey>,
): boolean {
  if (stepKey === 'create_account') return false
  if (completedKeys.has(stepKey)) return false
  return nextGuidedStepKey(completedKeys) !== stepKey
}

/** Kończy sesję guided i wraca na dashboard (checklistę). */
export function returnToOnboardingHub(
  navigate: (path: string) => void,
  setCurrentStep: (key: OnboardingStepKey | null) => void,
  stopGuided?: () => void,
): void {
  stopGuided?.()
  setCurrentStep(null)
  navigate(ONBOARDING_HUB_PATH)
}

/** Po ukończeniu kroku guided — wróć na dashboard (kolejny krok z checklisty). */
export function advanceGuidedFromStatus(
  status: { steps: Array<{ key: OnboardingStepKey; completed: boolean }> },
  navigate: (path: string) => void,
  setCurrentStep: (key: OnboardingStepKey | null) => void,
  completedStepKey: OnboardingStepKey,
  stopGuided?: () => void,
): void {
  if (!completedOnboardingKeys(status).has(completedStepKey)) return
  returnToOnboardingHub(navigate, setCurrentStep, stopGuided)
}

/** Następny zablokowany krok (do podpowiedzi). */
export function nextLockedGuidedStep(
  completedKeys: Set<OnboardingStepKey>,
): OnboardingStepKey | null {
  const next = nextGuidedStepKey(completedKeys)
  if (!next) return null
  const idx = ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE.indexOf(next)
  return idx >= 0 && idx < ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE.length - 1
    ? ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE[idx + 1]
    : null
}
